import * as React from "react";
import * as S from "@styled";
import useOuterClick, { useInnerClick } from "shared/hooks/useOuterClick";
import { handleHideDropdown } from "shared/styles/helpers";

const DropdownButton: React.FC<IDropdownButtonProps> = (props) => {

    const dropdownRef = useOuterClick(() => handleHideDropdown(props.id))
    const innerDropdownRef = useInnerClick(() => handleHideDropdown(props.id))

    return (
        <S.DropdownButton
            id={props.dropDownButtonId}
            style={props.dropDownButtonCustomStyles}
            className={props.dropDownButtonClassName}
            title={props.dropDownButtonTitle}
        >
            <S.DropdownContainer ref={dropdownRef}>
                {props.buttonComponent}
                <S.DropdownContent
                    ref={innerDropdownRef}
                    id={props.id}
                    style={props.dropDownContentCustomStyles}
                    pullRight={props.dropDownContentPullRight}
                >
                    {props.children}
                </S.DropdownContent>
            </S.DropdownContainer>
        </S.DropdownButton>
    )
}

export const DropdownButtonWithState: React.FC<IDropdownButtonWithStateProps> = (props) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (props.open && containerRef.current && contentRef.current) {
            const content = contentRef.current;
            const contentRect = content.getBoundingClientRect();
            const contentOverflow = contentRect.top + content.clientHeight - window.innerHeight;
            if (contentOverflow > 0 && window.innerHeight > content.clientHeight) {
                content.style.top = `-${contentOverflow}px`;
                content.style.right = `${containerRef.current.clientWidth}px`;
            }
        }
        
        const handleOuterClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                props.onHide();
            }
        };

        const removeOuterClickListerner = () => document.removeEventListener('click', handleOuterClick);

        if (props.open) {
            setTimeout(() =>document.addEventListener('click', handleOuterClick));
        } else {
            removeOuterClickListerner();
        }

        return () => removeOuterClickListerner();
    }, [props.open]);

    const getDropdownContentClassName = () => props.open ? 'show' : undefined;

    return (
        <S.DropdownButton
            id={props.dropDownButtonId}
            style={props.dropDownButtonCustomStyles}
            className={props.dropDownButtonClassName}
            title={props.dropDownButtonTitle}
        >
            <S.DropdownContainer ref={containerRef}>
                {props.buttonComponent}
                <S.DropdownContent
                    ref={contentRef}
                    className={getDropdownContentClassName()}
                    id={props.id}
                    style={props.dropDownContentCustomStyles}
                    pullRight={props.dropDownContentPullRight}
                >
                    {props.children}
                </S.DropdownContent>
            </S.DropdownContainer>
        </S.DropdownButton>
    );
}

interface IDropdownButtonProps extends React.PropsWithChildren {
    id: string;
    buttonComponent: JSX.Element | React.ReactElement;
    dropDownButtonTitle?: string;
    dropDownButtonId?: string;
    dropDownButtonClassName?: string;
    dropDownButtonCustomStyles?: React.CSSProperties;
    dropDownContentPullRight?: boolean;
    dropDownContentCustomStyles?: React.CSSProperties;
}

interface IDropdownButtonWithStateProps extends IDropdownButtonProps {
    open: boolean;
    onHide: () => void;
}

export default DropdownButton;